import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { CardDivider } from '@/components/atoms/Card';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { TooltipRows } from '@/components/atoms/Tooltip';
import { renderTrendIcon } from '@/components/atoms/TrendIcon/TrendIconUtils';
import ContentBlock from '@/components/molecules/ContentBlock';
import { MetricSummaryRow } from '@/components/molecules/MetricSummaryRow';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import type { SwimSpeedOverview, WelfareOverview } from '@/domain/cage-welfare-overview';

import { buildWelfareRows, type WelfareRowKey } from '../helpers/welfareSummaryRows';

import ViewButton from './ViewButton';

import styles from './cardParts.module.scss';

const WELFARE_ROW_TRACKING_ID: Record<WelfareRowKey, string> = {
    wounds: 'cage_dashboard_welfare_wounds',
    swimSpeed: 'cage_dashboard_welfare_swim_speed',
};

interface WelfareSummaryCardProps {
    welfare: WelfareOverview | undefined;
    swimSpeed: SwimSpeedOverview | undefined;
    isSmolt: boolean;
    /** Either the FISH_SPEED or the BEHAVIOR licence. */
    hasSpeedAccess: boolean;
    isLoading: boolean;
    /** Set when the welfare overview failed to load: the error state with Try again replaces the rows. */
    loadError?: RetryProps | null;
    welfareUrl: string;
    woundsUrl: string;
    swimSpeedUrl: string;
}

/**
 * Wounds and swim speed, or for smolt wounds alone.
 *
 * Only the grow-out rows lead anywhere: the smolt card never had per-row links, and the
 * header's action already opens the welfare module.
 */
export default function WelfareSummaryCard(props: WelfareSummaryCardProps) {
    const {
        welfare,
        swimSpeed,
        isSmolt,
        hasSpeedAccess,
        isLoading,
        loadError,
        welfareUrl,
        woundsUrl,
        swimSpeedUrl,
    } = props;
    const { t, i18n } = useTranslation();

    const rows = buildWelfareRows({
        welfare,
        swimSpeed,
        isSmolt,
        hasSpeedAccess,
        locale: i18n.language,
        t,
    });

    const rowUrl: Partial<Record<WelfareRowKey, string>> = isSmolt
        ? {}
        : { wounds: woundsUrl, ...(hasSpeedAccess && { swimSpeed: swimSpeedUrl }) };

    return (
        <ContentBlock
            title={t('common.labels.welfare')}
            spacing="none"
            className={styles.listCard}
            headerActions={<ViewButton to={welfareUrl} />}>
            {loadError && <SomethingHappened {...loadError} />}
            {!loadError &&
                rows.map((row) => {
                    const url = rowUrl[row.key];
                    return (
                        <Fragment key={row.key}>
                            <MetricSummaryRow
                                className={styles.listRow}
                                label={row.label}
                                value={row.value}
                                trend={renderTrendIcon(row.trend)}
                                isLoading={isLoading}
                                tooltipDisabled={isLoading}
                                tooltip={
                                    Array.isArray(row.tooltip) ? (
                                        <TooltipRows rows={row.tooltip} />
                                    ) : (
                                        row.tooltip
                                    )
                                }
                                action={
                                    url && (
                                        <Button
                                            to={url}
                                            variant="tertiary"
                                            trackingId={WELFARE_ROW_TRACKING_ID[row.key]}
                                            iconOnly
                                            aria-label={t('common.actions.view')}
                                            icon={
                                                <SvgIcon
                                                    color="currentColor"
                                                    icon={ArrowRightLine}
                                                />
                                            }
                                        />
                                    )
                                }
                            />
                            <CardDivider />
                        </Fragment>
                    );
                })}
        </ContentBlock>
    );
}
